import { Component, HostListener, inject, signal, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from './components/layout/sidebar.component';
import { ToastComponent } from './components/ui/toast/toast.component';
import { EnvironmentBannerComponent } from './components/ui/environment-banner/environment-banner.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter } from 'rxjs/operators';
import { LucideAngularModule, Menu } from 'lucide-angular';
import { IdleService } from './services/idle.service';
import { ApiService } from './services/api.service';
import { LoggerService } from './services/logger.service';
import { PollingService } from './services/polling.service';
import { STORAGE_KEYS } from './constants/storage-keys';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, SidebarComponent, ToastComponent, LucideAngularModule, TranslocoPipe, EnvironmentBannerComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent {
  readonly Menu = Menu;

  private router: Router = inject(Router);

  // Initialize showLayout based on current route to prevent ExpressionChangedAfterItHasBeenCheckedError
  showLayout = signal<boolean>(this.getInitialLayoutState());

  @ViewChild(SidebarComponent) sidebarComponent?: SidebarComponent;
  // Initialize idle service and other core services for auth flow
  private idleService = inject(IdleService);
  private apiService = inject(ApiService);
  private logger = inject(LoggerService);
  private pollingService = inject(PollingService);

  // Touch gesture state for mobile left-edge swipe
  private touchStartX = 0;
  private touchStartY = 0;
  private touchTracking = false;
  private isHorizontalSwipe = false;

  constructor() {
    // // Initialize background polling service
    // this.initializePollingService();

    // Handle layout visibility based on route
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects || event.url;

        // Capture lead_source query param (e.g. /onboarding?lead_source=paid) on
        // app start so it survives navigation and can be sent to HubSpot later.
        // The app uses hash-based routing, so window.location.search is always
        // empty; the router's own parsed url (with the hash prefix already
        // stripped) is the reliable source for query params here.
        this.captureLeadSource(url);

        // Hide layout on landing, login and onboarding pages
        const isPublicPage =
          url === '/' ||
          url.startsWith('/login') ||
          url.startsWith('/onboarding') ||
          url.startsWith('/first-time');
        this.showLayout.set(!isPublicPage);
      });

    // Listen for toggle menu events from page headers
    window.addEventListener('toggleMobileMenu', () => {
      this.toggleMobileMenu();
    });
  }

  /**
   * Reads lead_source from the given (already hash-stripped) router url, if
   * present, and persists it to sessionStorage. The param can appear on any
   * route (not just /onboarding), so this runs on every navigation.
   */
  private captureLeadSource(url: string): void {
    const queryString = url.includes('?') ? url.substring(url.indexOf('?') + 1) : '';
    const leadSource = new URLSearchParams(queryString).get('lead_source');
    if (leadSource) {
      sessionStorage.setItem(STORAGE_KEYS.LEAD_SOURCE, leadSource);
    }
  }

  /**
   * Get initial layout state based on current route
   */
  private getInitialLayoutState(): boolean {
    const currentUrl = this.router.url;
    const isPublicPage =
      currentUrl === '/' ||
      currentUrl.startsWith('/login') ||
      currentUrl.startsWith('/onboarding') ||
      currentUrl.startsWith('/first-time');
    return !isPublicPage;
  }

  toggleMobileMenu() {
    this.sidebarComponent?.toggleMobileMenu();
  }

  isMobileMenuOpen() {
    return this.sidebarComponent?.isMobileMenuOpen() ?? false;
  }

  // --- Mobile left-edge swipe to open sidebar ---

  private isMobileViewport(): boolean {
    return window.innerWidth <= 768;
  }

  @HostListener('document:touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    if (!this.isMobileViewport() || this.isMobileMenuOpen()) {
      this.touchTracking = false;
      this.isHorizontalSwipe = false;
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      this.touchTracking = false;
      this.isHorizontalSwipe = false;
      return;
    }

    // Only start tracking if gesture begins very close to the left edge (30px)
    if (touch.clientX > 30) {
      this.touchTracking = false;
      this.isHorizontalSwipe = false;
      return;
    }

    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchTracking = true;
    this.isHorizontalSwipe = false;
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (!this.touchTracking || !this.isMobileViewport() || this.isMobileMenuOpen()) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = Math.abs(touch.clientY - this.touchStartY);
    const absDeltaX = Math.abs(deltaX);

    // Detect if this is a horizontal swipe
    if (absDeltaX > 10 || deltaY > 10) {
      this.isHorizontalSwipe = absDeltaX > deltaY;
    }

    // If the user scrolls vertically a lot, cancel the gesture
    if (deltaY > 30 && deltaY > absDeltaX) {
      this.touchTracking = false;
      this.isHorizontalSwipe = false;
      return;
    }

    // Prevent default scrolling when performing horizontal swipe gesture
    if (this.isHorizontalSwipe && deltaX > 10) {
      event.preventDefault();
    }

    // Trigger open when swiping right beyond threshold
    if (deltaX > 60) {
      this.touchTracking = false;
      this.isHorizontalSwipe = false;
      this.toggleMobileMenu();
    }
  }

  @HostListener('document:touchend')
  onTouchEnd() {
    this.touchTracking = false;
    this.isHorizontalSwipe = false;
  }

  /**
   * Initialize background polling service with tasks
   */
  private initializePollingService(): void {
    this.logger.log('Initializing background polling service');

    // Example 1: Poll customers list every 30 seconds
    // Uncomment and customize based on your needs
    /*
    this.pollingService.addTask({
      id: 'poll-customers',
      name: 'Poll Customers List',
      intervalMs: 30000, // 30 seconds
      apiCall: () => this.apiService.getCustomers(),
      parseData: (response) => {
        // Extract just the items array if response has pagination
        return {
          customers: response.items || response.data || response,
          count: response.totalCount || response.items?.length || 0,
          lastUpdated: new Date().toISOString()
        };
      },
      storageKey: 'customers_cache',
      onSuccess: (data) => {
        this.logger.log(`Customers polled: ${data.count} customers found`);
      },
      onError: (error) => {
        this.logger.error('Failed to poll customers:', error);
      },
      initialDelayMs: 5000, // Wait 5 seconds before first poll
      enabled: false // Disabled by default, enable when needed
    });
    */

    // Example 2: Poll system notifications every 60 seconds
    // Uncomment and customize based on your needs
    /*
    this.pollingService.addTask({
      id: 'poll-notifications',
      name: 'Poll Notifications',
      intervalMs: 60000, // 60 seconds (1 minute)
      apiCall: () => this.apiService.getCurrentUser(),
      parseData: (response) => {
        // Extract notifications from user data
        return {
          notifications: response.notifications || [],
          unreadCount: response.unreadNotifications || 0
        };
      },
      storageKey: 'user_notifications',
      onSuccess: (data) => {
        if (data.unreadCount > 0) {
          this.logger.log(`You have ${data.unreadCount} unread notifications`);
          // You could trigger a notification toast here
        }
      },
      enabled: false // Disabled by default
    });
    */

    // Example 3: Health check ping every 5 minutes
    // Uncomment and customize based on your needs
    /*
    this.pollingService.addTask({
      id: 'health-check',
      name: 'System Health Check',
      intervalMs: 300000, // 5 minutes
      apiCall: () => this.apiService.getCurrentUser(), // Replace with actual health endpoint
      parseData: (response) => {
        return {
          status: 'healthy',
          timestamp: new Date().toISOString(),
          serverTime: response.serverTime
        };
      },
      storageKey: 'system_health',
      storeInLocalStorage: false, // Don't store health checks
      onError: (error) => {
        this.logger.error('System health check failed:', error);
        // Could trigger an alert or notification
      },
      enabled: false // Disabled by default
    });
    */

    // Start the polling service
    // Note: Tasks won't run until they are enabled
    this.pollingService.start();

    this.logger.log('Background polling service initialized');
  }
}
